import React, { useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { useTranslation } from "../i18n/I18nContext";
import { listActiveStudents, listFrequencies } from "../services/student";
import {
  downloadExtrasReceipt,
  downloadReceipt,
  downloadRemainderReceipt,
  listStudentExtraconfigs,
  payExtrasMultiple,
  payPropina,
} from "../services/payment";
import { levelKey } from "../utils/classesLabels";
import { getSucursalInfo, getUserInfo } from "../services/auth";
import { listPaymentTypes } from "../services/paymentTypes";
import {
  buildPropinaRows,
  formatCurrencyMZN,
  getPropinaRemaining,
  monthName,
  PAYMENT_METHODS,
} from "../utils/paymentHelpers";

const currentYear = new Date().getFullYear();
const todayIso = () => new Date().toISOString().split("T")[0];

const ConfirmarPagamentoLayer = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { paymentId } = useParams();

  const [registration, setRegistration] = useState(location.state?.registration || null);
  const [loadingFallback, setLoadingFallback] = useState(!location.state?.registration);
  const [notFound, setNotFound] = useState(false);

  const payment = useMemo(
    () => registration?.payments?.find((p) => String(p.id) === String(paymentId)) || null,
    [registration, paymentId],
  );

  // Segunda linha de defesa (a fonte de verdade é o 400 do backend em PUT /payment/pay/:id):
  // a taxa de Inscrição/Renovação desta registration, se ainda não estiver totalmente paga.
  // Sem entrada correspondente (registration antiga, sem `code` em taxPayments), fica null —
  // nunca bloqueia retroactivamente.
  const registrationTaxBlocking = useMemo(() => {
    if (!registration) return null;
    return (
      (registration.taxPayments || []).find(
        (tp) =>
          (tp.code === "registrationValue" || tp.code === "recurigRegistrationValue") &&
          Number(tp.status) !== 1,
      ) || null
    );
  }, [registration]);

  const [paymentState, setPaymentState] = useState(null);
  const [extrasForPayment, setExtrasForPayment] = useState(location.state?.extrasForPayment || []);
  const [paymentType, setPaymentType] = useState(location.state?.forcePaymentType || "all");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [paymentDate, setPaymentDate] = useState(todayIso());
  const [forgiveFine, setForgiveFine] = useState(false);
  const [fineCopy, setFineCopy] = useState(0);
  const [amountPaid, setAmountPaid] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [loadingReceipt, setLoadingReceipt] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState(PAYMENT_METHODS);

  // Tipos de Pagamento por sucursal (SGE_PAYMENT_TYPES) - PAYMENT_METHODS só
  // é usado como reserva se a chamada falhar ou a sucursal não tiver tipos.
  useEffect(() => {
    listPaymentTypes()
      .then((resp) => {
        const names = (resp.data || []).map((pt) => pt.name).filter(Boolean);
        if (names.length) setPaymentMethods(names);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!payment) return;
    setPaymentState({ ...payment });
    if (!location.state?.forcePaymentType) {
      const extras = location.state?.extrasForPayment || payment.extrasForMonth || [];
      setPaymentType(extras.some((e) => Number(e.status) !== 1) ? "all" : "propina");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payment]);

  useEffect(() => {
    if (registration) return;
    Promise.all([listActiveStudents(currentYear), listStudentExtraconfigs(), listFrequencies()])
      .then(([regResp, extraResp, freqResp]) => {
        const extras = (extraResp.data || []).filter((item) => Number(item.year) === Number(currentYear));
        const rows = buildPropinaRows(regResp.data || [], extras, freqResp.data || [], currentYear);
        const found = rows.find((r) => r.payments.some((p) => String(p.id) === String(paymentId)));
        if (found) {
          setRegistration(found);
          if (!extrasForPayment.length) {
            const p = found.payments.find((pp) => String(pp.id) === String(paymentId));
            setExtrasForPayment(p?.extrasForMonth || []);
          }
        } else {
          setNotFound(true);
        }
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoadingFallback(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const extrasTotal = useMemo(
    () => extrasForPayment.reduce((sum, e) => sum + (parseFloat(e.total) || 0), 0),
    [extrasForPayment],
  );

  const hasUnpaidExtras = extrasForPayment.some((e) => Number(e.status) !== 1);

  // "Pagar Somente Extras" (ListaPropinasLayer.handlePayExtrasOnly) locks this screen to the
  // extras-only flow — propina isn't part of that transaction, so those options must stay
  // disabled rather than just defaulted, otherwise the user could switch back into a
  // propina payment this screen was never set up to submit correctly.
  const onlyExtrasForced = location.state?.forcePaymentType === "extras";
  const disablePropinaOption = onlyExtrasForced;
  const disableExtrasOption = !hasUnpaidExtras;
  const disableAllOption = disablePropinaOption || disableExtrasOption;

  // "Pagar Remanescente" (ListaPropinasLayer.handlePayRemainder) — settles what's still
  // owed on a propina/multa already left Parcial. Like the extras-only flow, this locks
  // the screen to a single-purpose transaction: extrasForPayment is always empty (extras
  // stay on their own "Pagar Extras" flow), so the propina/extras/all radio choice makes
  // no sense here and is hidden entirely instead of just disabled.
  const remainderMode = location.state?.forcePaymentType === "remainder";

  const toggleForgiveFine = (checked) => {
    setForgiveFine(checked);
    if (checked) {
      const removed = paymentState.fine;
      setFineCopy(removed);
      setPaymentState((p) => {
        const total = parseFloat(payment.total);
        return {
          ...p,
          fine: 0,
          total,
          // combinedTotal ("Pagar Tudo") has to be recomputed here too — it's what
          // totalAPagar reads for paymentType "all", the default, so leaving it stale
          // meant the amount to pay never moved when this checkbox was toggled.
          combinedTotal: total + (parseFloat(p.extrasTotal) || 0),
          remaining: removed,
        };
      });
    } else {
      setPaymentState((p) => {
        const fine = parseInt(fineCopy, 10) || 0;
        const total = parseFloat(payment.total) || 0;
        return {
          ...p,
          fine,
          total,
          combinedTotal: total + fine + (parseFloat(p.extrasTotal) || 0),
          remaining: undefined,
        };
      });
    }
  };

  const totalAPagar = useMemo(() => {
    if (!paymentState) return 0;
    if (paymentType === "remainder") return getPropinaRemaining(paymentState);
    if (paymentType === "propina") return (parseFloat(paymentState.total) || 0) + (parseFloat(paymentState.fine) || 0);
    if (paymentType === "extras") return extrasTotal;
    return paymentState.combinedTotal;
  }, [paymentState, paymentType, extrasTotal]);

  // Partial payment only makes sense when nothing else besides the propina itself is being
  // charged in this transaction — extras (and the fine, once forgiven) stay all-or-nothing.
  // Remainder mode is always eligible: it never touches extras and its own "amount owed"
  // (totalAPagar above) is already just what's left of the propina/multa.
  const canPayPartially =
    !forgiveFine && (extrasForPayment.length === 0 || paymentType === "propina" || paymentType === "remainder");

  const amountPaidValue = useMemo(() => {
    if (amountPaid === "") return totalAPagar;
    return Math.max(0, Math.min(parseFloat(amountPaid) || 0, totalAPagar));
  }, [amountPaid, totalAPagar]);

  const isAmountPaidOverMax = amountPaid !== "" && parseFloat(amountPaid) > totalAPagar;
  const remainingAfterPayment = Math.max(0, totalAPagar - amountPaidValue);

  useEffect(() => {
    if (!canPayPartially) setAmountPaid("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canPayPartially]);

  const handleConfirm = async () => {
    if (!paymentMethod || !paymentState) return;
    if (paymentType !== "extras" && registrationTaxBlocking) {
      toast.error(t("propina.error.registrationTaxPending"));
      return;
    }
    setSubmitting(true);
    try {
      if (paymentType === "extras") {
        const unpaidExtras = extrasForPayment
          .filter((e) => Number(e.status) === 0)
          .map((e) => ({
            reference: e.reference,
            extraId: e.extraId || e.id,
            description: e.taxName,
            monthlyAmount: e.recurringAmount || e.total,
            month: e.month || payment.month,
          }));
        if (unpaidExtras.length === 0) {
          toast.error(t("propina.extras.noneUnpaid"));
          setSubmitting(false);
          return;
        }
        await payExtrasMultiple({
          studentId: registration.studentId,
          sucursalId: getSucursalInfo()?.id,
          paymentMethod,
          updatedBy: getUserInfo()?.id,
          paymentDate,
          extras: unpaidExtras,
        });
      } else {
        const payOnlyPropina = paymentType === "propina";
        const payload = {
          paymentMethod,
          payWithoutFine: forgiveFine,
          registrationId: registration.registrationId,
          updatedBy: getUserInfo()?.id,
          studentId: registration.studentId,
          canceledBy: getUserInfo()?.id,
          remaining: paymentState.remaining,
        };
        if (!paymentState.fine || paymentState.fine <= 0) {
          payload.paymentDate = paymentDate;
        }
        // Partial payment: only sent when strictly below the total being charged — the
        // backend derives the remaining debt from this value.
        if (canPayPartially && amountPaidValue > 0 && amountPaidValue < totalAPagar) {
          payload.amountPaid = amountPaidValue;
        }
        const shouldPayExtras = !payOnlyPropina && extrasForPayment.length > 0;
        if (shouldPayExtras) {
          const unpaidExtras = extrasForPayment
            .filter((e) => Number(e.status) === 0)
            .map((e) => ({
              reference: e.reference,
              extraId: e.extraId || e.id,
              description: e.taxName,
              monthlyAmount: e.recurringAmount || e.total,
              month: e.month,
            }));
          if (unpaidExtras.length > 0) payload.extras = unpaidExtras;
        }
        await payPropina(paymentId, payload);
      }
      setSuccess(true);
    } catch (err) {
      toast.error(t("propina.error.generic"));
    } finally {
      setSubmitting(false);
    }
  };

  const backToPayments = () => navigate("/pagamentos/propinas");

  const handleViewReceipt = async () => {
    setLoadingReceipt(true);
    try {
      let resp;
      if (remainderMode) {
        // amountPaidValue still holds what was actually charged in the just-completed
        // transaction (state isn't reset on success) — the backend record only keeps the
        // cumulative paidAmount, so this transaction's own figure has to be passed through.
        resp = await downloadRemainderReceipt(paymentId, { amountPaid: amountPaidValue });
      } else if (paymentType === "extras") {
        // "Pagar Somente Extras" never touches the propina itself, so /report/student/payments
        // (which infers paid/pending from the propina's own status) can't be used here — it
        // would read the untouched propina as still unpaid and mark every extra pending too.
        // Use the dedicated extras-only receipt endpoint instead; extrasForPayment at this
        // point is exactly the set that was just paid (see handlePayExtrasOnly).
        const extrasForPdf = extrasForPayment.map((e) => ({
          description: e.taxName,
          monthlyAmount: e.recurringAmount || e.total,
          month: e.month || payment.month,
          year: e.year || payment.year,
        }));
        resp = await downloadExtrasReceipt(registration.registrationId, {
          extras: extrasForPdf,
          paymentMethod,
          paymentDate,
        });
      } else {
        // Mirrors sge_infantil_web's `payOnlyPropina ? extra.status : 1`: extras only stay
        // "unpaid" on the receipt when this transaction paid propina alone ("propina"); "all"
        // just settled them together with the propina, so force status=1 for that case.
        const extrasForPdf = extrasForPayment.map((e) => ({
          extraId: e.extraId || e.id,
          description: e.taxName,
          monthlyAmount: e.recurringAmount || e.total,
          status: paymentType === "propina" ? e.status : 1,
        }));
        resp = await downloadReceipt(paymentId, extrasForPdf);
      }
      if (resp.data?.pdf) {
        navigate("/documentos/pdf", {
          state: {
            url: resp.data.pdf,
            title: remainderMode
              ? t("propina.documento.receiptRemainder", { month: monthName(payment.month) })
              : t("propina.documento.receipt", { month: monthName(payment.month), year: payment.year }),
            filename: remainderMode
              ? `comprovativo-remanescente-${registration.name}.pdf`
              : `comprovativo-${registration.name}.pdf`,
            // The payment was already confirmed at this point, so browser-history
            // back(-1) would drop the user back on this "pagar" screen instead of
            // the propinas list — send them straight to the list instead.
            backTo: "/pagamentos/propinas",
          },
        });
      } else {
        toast.error(t("propina.error.print"));
      }
    } catch (err) {
      toast.error(t("propina.error.print"));
    } finally {
      setLoadingReceipt(false);
    }
  };

  if (loadingFallback) {
    return (
      <div className='row gy-4'>
        <div className='col-12 text-center text-secondary-light py-64'>{t("estudante.list.loading")}</div>
      </div>
    );
  }

  if (notFound || !registration || !payment || !paymentState) {
    return (
      <div className='row gy-4'>
        <div className='col-12'>
          <div className='card radius-12'>
            <div className='card-body text-center py-64'>
              <p className='text-secondary-light mb-16'>{t("propina.confirm.notFound")}</p>
              <button type='button' className='btn btn-primary-600' onClick={backToPayments}>
                <i className='ri-arrow-left-line' /> {t("propina.action.backToPayments")}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='row gy-4'>
      <ToastContainer />
      <div className='col-12'>
        <div className='card radius-12'>
          <div className='card-body'>
            {!success ? (
              <>
                <div className='alert alert-info d-flex flex-column mb-24'>
                  <strong>{remainderMode ? t("propina.confirm.remainderTitle") : t("propina.confirm.title")}</strong>
                  <span className='text-sm'>
                    {remainderMode ? t("propina.confirm.remainderSubtitle") : t("propina.confirm.subtitle")}
                  </span>
                </div>

                <h6 className='mb-16'>{registration.name}</h6>
                <div className='table-responsive mb-24'>
                  <table className='table table-bordered table-sm mb-0 bg-base'>
                    <tbody>
                      <tr>
                        <th className='w-25 bg-neutral-50'>{t(levelKey("propina.col.level"))}</th>
                        <td>{registration.level || "-"}</td>
                        <th className='w-25 bg-neutral-50'>{t("propina.col.enrolledAt")}</th>
                        <td>
                          {registration.createdAt
                            ? new Date(registration.createdAt).toLocaleDateString("pt-PT")
                            : "-"}
                        </td>
                      </tr>
                      <tr>
                        <th className='bg-neutral-50'>{t("propina.col.limitDate")}</th>
                        <td>
                          {payment.limit ? new Date(payment.limit).toLocaleDateString("pt-PT") : "-"}
                        </td>
                        <th className='bg-neutral-50'>{t("propina.col.year")}</th>
                        <td>{payment.year}</td>
                      </tr>
                      <tr>
                        <th className='bg-neutral-50'>{t("propina.col.month")}</th>
                        <td className='notranslate'>{monthName(payment.month)}</td>
                        <th className='bg-neutral-50'>{t("propina.detail.monthlyValue")}</th>
                        <td>{formatCurrencyMZN(payment.total)}</td>
                      </tr>
                      {payment.fine > 0 && (
                        <tr>
                          <th className='bg-neutral-50'>{t("propina.detail.fine")}</th>
                          <td colSpan={3}>{formatCurrencyMZN(paymentState.fine)}</td>
                        </tr>
                      )}
                      {extrasForPayment.length > 0 && (
                        <tr>
                          <th className='bg-neutral-50'>{t("propina.col.extras")}</th>
                          <td colSpan={3}>
                            <div className='d-flex flex-column gap-8'>
                              {extrasForPayment.map((extra, idx) => (
                                <div key={idx} className='d-flex align-items-center gap-2'>
                                  <span
                                    className={
                                      Number(extra.status) === 1
                                        ? "px-8 py-2 radius-8 text-xs bg-success-focus text-success-main"
                                        : "px-8 py-2 radius-8 text-xs bg-warning-focus text-warning-main"
                                    }
                                  >
                                    {Number(extra.status) === 1 ? t("propina.status.paid") : t("propina.status.unpaid")}
                                  </span>
                                  <strong>{extra.taxName || t("propina.extras.default")}</strong>
                                  <span>{formatCurrencyMZN(extra.total)}</span>
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div className='row gy-3 mb-16'>
                  <div className='col-sm-6'>
                    <div
                      className={`p-16 radius-8 text-center border ${
                        Number(payment.status) === 1 ? "bg-success-focus border-success-100" : "bg-primary-50 border-primary-100"
                      }`}
                    >
                      <span className='text-secondary-light text-sm d-block mb-4'>
                        {remainderMode ? t("propina.summary.alreadyPaid") : t("propina.summary.propinaTotal")}
                      </span>
                      <strong className={Number(payment.status) === 1 ? "text-success-main text-xl" : "text-primary-600 text-xl"}>
                        {remainderMode
                          ? // paymentState.paidAmount is only ever populated when the propina itself was
                            // paid partially — it stays null/undefined for the "propina fully paid, only
                            // the fine is left" case, which would wrongly show 0 here. Derive it instead:
                            // propina+fine minus what's still owed (getPropinaRemaining) always lands on
                            // the actual amount already paid, whichever of the two cases applies.
                            formatCurrencyMZN(
                              (parseFloat(paymentState.total) || 0) +
                                (parseFloat(paymentState.fine) || 0) -
                                getPropinaRemaining(paymentState),
                            )
                          : formatCurrencyMZN((parseFloat(paymentState.total) || 0) + (parseFloat(paymentState.fine) || 0))}
                      </strong>
                    </div>
                  </div>
                  {extrasForPayment.length > 0 && hasUnpaidExtras && (
                    <div className='col-sm-6'>
                      <div className='p-16 radius-8 text-center bg-warning-focus border border-warning-100'>
                        <span className='text-secondary-light text-sm d-block mb-4'>
                          {t("propina.summary.extrasTotal")}
                        </span>
                        <strong className='text-warning-main text-xl'>{formatCurrencyMZN(extrasTotal)}</strong>
                      </div>
                    </div>
                  )}
                </div>

                <div
                  className={`p-24 radius-8 text-center mb-24 border ${
                    paymentType === "extras" ? "bg-warning-focus border-warning-200" : "bg-success-focus border-success-200"
                  }`}
                  style={{ borderWidth: 2 }}
                >
                  <span className='text-secondary-light text-sm d-block mb-8'>{t("propina.summary.totalToPay")}</span>
                  <strong className={paymentType === "extras" ? "text-warning-main" : "text-success-main"} style={{ fontSize: 32 }}>
                    {formatCurrencyMZN(totalAPagar)}
                  </strong>
                </div>

                <div className='row gy-3'>
                  <div className='col-sm-6'>
                    <label className='form-label'>{t("propina.field.paymentMethod")} <span className='required-asterisk'>*</span></label>
                    <select
                      className='form-control form-select'
                      value={paymentMethod}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                    >
                      <option value=''>{t("estudante.select.placeholder")}</option>
                      {paymentMethods.map((m) => (
                        <option key={m} value={m}>
                          {m}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className='col-sm-6'>
                    <label className='form-label'>{t("propina.field.paymentDate")}</label>
                    <div className="icon-field">
                      <span className="icon">
                        <Icon icon="solar:calendar-outline" />
                      </span>
                      <input
                        type='date'
                        className='form-control'
                        value={paymentDate}
                        onChange={(e) => setPaymentDate(e.target.value)}
                      />
                    </div>
                  </div>

                  {!remainderMode && (
                  <div className='col-12'>
                    <label className='form-label'>{t("propina.field.paymentOption")}</label>
                    <div className='d-flex flex-column gap-8'>
                      <div className='form-check'>
                        <input
                          id='paymentType-all'
                          type='radio'
                          className='form-check-input'
                          checked={paymentType === "all"}
                          disabled={disableAllOption}
                          onChange={() => setPaymentType("all")}
                        />
                        <label
                          htmlFor='paymentType-all'
                          className={`form-check-label${disableAllOption ? " text-secondary-light" : ""}`}
                        >
                          {t("propina.field.paymentOption.all", {
                            value: formatCurrencyMZN(paymentState.combinedTotal),
                          })}
                        </label>
                      </div>
                      <div className='form-check'>
                        <input
                          id='paymentType-propina'
                          type='radio'
                          className='form-check-input'
                          checked={paymentType === "propina"}
                          disabled={disablePropinaOption}
                          onChange={() => setPaymentType("propina")}
                        />
                        <label
                          htmlFor='paymentType-propina'
                          className={`form-check-label${disablePropinaOption ? " text-secondary-light" : ""}`}
                        >
                          {t("propina.field.paymentOption.propina", {
                            value: formatCurrencyMZN(paymentState.total),
                          })}
                        </label>
                      </div>
                      <div className='form-check'>
                        <input
                          id='paymentType-extras'
                          type='radio'
                          className='form-check-input'
                          checked={paymentType === "extras"}
                          disabled={disableExtrasOption}
                          onChange={() => setPaymentType("extras")}
                        />
                        <label
                          htmlFor='paymentType-extras'
                          className={`form-check-label${disableExtrasOption ? " text-secondary-light" : ""}`}
                        >
                          {t("propina.field.paymentOption.extras", { value: formatCurrencyMZN(extrasTotal) })}
                        </label>
                      </div>
                    </div>
                  </div>
                  )}

                  {/* Gated on the original payment.fine, not paymentState.fine — toggling the
                      checkbox on zeroes paymentState.fine (so the total drops), and gating
                      visibility on that same field made the checkbox vanish the instant it was
                      checked, leaving no way to uncheck it. */}
                  {!remainderMode && payment.fine > 0 && paymentType !== "extras" && (
                    <div className='col-12'>
                      <div className='form-check'>
                        <input
                          id='forgiveFine'
                          type='checkbox'
                          className='form-check-input'
                          checked={forgiveFine}
                          onChange={(e) => toggleForgiveFine(e.target.checked)}
                        />
                        <label htmlFor='forgiveFine' className='form-check-label'>
                          {t("propina.field.forgiveFine")}
                        </label>
                      </div>
                    </div>
                  )}

                  {canPayPartially && (
                    <div className='col-sm-6'>
                      <label className='form-label'>{t("propina.field.amountPaid")} <span className='required-asterisk'>*</span></label>
                      <div className='icon-field'>
                        <span className='icon'>
                          <Icon icon='solar:dollar-minimalistic-outline' />
                        </span>
                        <input
                          type='number'
                          min='0.01'
                          max={totalAPagar}
                          step='0.01'
                          className='form-control'
                          value={amountPaid === "" ? totalAPagar.toFixed(2) : amountPaid}
                          onChange={(e) => setAmountPaid(e.target.value)}
                        />
                      </div>
                      {isAmountPaidOverMax ? (
                        <span className='text-danger-main text-xs'>
                          {t("propina.field.amountPaid.overMax", { value: formatCurrencyMZN(totalAPagar) })}
                        </span>
                      ) : (
                        remainingAfterPayment > 0.01 && (
                          <span className='text-warning-main text-xs'>
                            {t("propina.field.amountPaid.remaining", {
                              value: formatCurrencyMZN(remainingAfterPayment),
                            })}
                          </span>
                        )
                      )}
                    </div>
                  )}
                </div>

                {paymentType !== "extras" && registrationTaxBlocking && (
                  <div className='alert alert-danger d-flex align-items-center gap-8 mt-16'>
                    <Icon icon='solar:lock-keyhole-outline' className='text-lg flex-shrink-0' />
                    {t("propina.error.registrationTaxPending")}
                  </div>
                )}

                <div className='d-flex align-items-center justify-content-end gap-8 mt-24'>
                  <button type='button' className='btn btn-neutral-500 border-neutral-100 px-32' onClick={backToPayments}>
                    {t("global.cancel")}
                  </button>
                  <button
                    type='button'
                    className='btn btn-primary-600 px-32'
                    onClick={handleConfirm}
                    disabled={
                      submitting ||
                      !paymentMethod ||
                      (paymentType === "extras" ? !extrasTotal : !paymentState.total) ||
                      (canPayPartially && (amountPaidValue <= 0 || isAmountPaidOverMax)) ||
                      (paymentType !== "extras" && !!registrationTaxBlocking)
                    }
                  >
                    {submitting ? "..." : <><i className='ri-check-line' /> {t("global.confirm")}</>}
                  </button>
                </div>
              </>
            ) : (
              <div className='text-center py-40'>
                <span className='w-64-px h-64-px bg-success-focus text-success-main rounded-circle d-inline-flex justify-content-center align-items-center mb-16 text-2xl'>
                  <i className='ri-check-line' />
                </span>
                <h6 className='text-md text-neutral-600'>{t("propina.confirm.success.title")}</h6>
                <p className='text-neutral-400 text-sm mb-24'>{t("propina.confirm.success.subtitle")}</p>
                <div className='d-flex align-items-center justify-content-center gap-8'>
                  <button
                    type='button'
                    className='btn btn-outline-primary-600 px-32'
                    onClick={handleViewReceipt}
                    disabled={loadingReceipt}
                  >
                    {loadingReceipt ? "..." : t("propina.action.receipt")}
                  </button>
                  <button type='button' className='btn btn-primary-600 px-32' onClick={backToPayments}>
                    <i className='ri-arrow-left-line' /> {t("propina.action.backToPayments")}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmarPagamentoLayer;
